<script setup lang="ts">
import { computed } from 'vue';
import {
    BBtn, BIcon, BMenu, BListItem, BListItemTitle,
} from '@/design-system';
import { useTranslation, useConfirmation } from '@/composables';
import type { TableActionItemType } from '@/types/TableActionItemType';

export type PropsType = {
    showEditButton?: boolean;
    showDuplicateButton?: boolean;
    showDeleteAction?: boolean;
    customActions?: TableActionItemType[];
    editIcon?: string;
    duplicateIcon?: string;
    deleteIcon?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    showEditButton: true,
    showDuplicateButton: true,
    showDeleteAction: true,
    customActions: () => [],
    editIcon: 'mdi-pencil',
    duplicateIcon: 'mdi-content-copy',
    deleteIcon: 'mdi-delete-outline',
});

const emit = defineEmits<{
    edit: [];
    duplicate: [];
    delete: [];
    action: [action: string];
}>();

const { t } = useTranslation();
const { onConfirm } = useConfirmation();

const hasMenuActions = computed(() => {
    return props.showDuplicateButton || props.showDeleteAction || props.customActions.length > 0;
});

const handleEdit = () => {
    emit('edit');
};

const handleDuplicate = () => {
    emit('duplicate');
};

const handleDelete = async () => {
    if (await onConfirm()) {
        emit('delete');
    }
};

const handleCustomAction = async (action: TableActionItemType) => {
    if (action.requiresConfirmation) {
        if (await onConfirm()) {
            emit('action', action.key);
        }
    } else {
        emit('action', action.key);
    }
};
</script>

<template>
    <div class="d-flex align-center justify-end ga-2">
        <BBtn
            v-if="showEditButton"
            size="small"
            density="comfortable"
            icon
            color="transparent"
            @click="handleEdit"
        >
            <BIcon color="data-table-actions">
                {{ editIcon }}
            </BIcon>
        </BBtn>

        <BMenu
            v-if="hasMenuActions"
            class="ml-auto"
            activator-icon="mdi-dots-vertical"
            activator-btn-is-icon
            activator-btn-variant="text"
            activator-btn-size="small"
            activator-btn-rounded="circle"
            activator-btn-density="comfortable"
        >
            <template #list-items>
                <BListItem
                    v-if="showDuplicateButton"
                    density="compact"
                    @click="handleDuplicate"
                >
                    <BListItemTitle class="d-flex align-center gc-2">
                        <BIcon
                            size="small"
                            color="data-table-actions"
                        >
                            {{ duplicateIcon }}
                        </BIcon>
                        <span class="label">{{ t('common.duplicate') }}</span>
                    </BListItemTitle>
                </BListItem>

                <BListItem
                    v-for="action in customActions"
                    :key="action.key"
                    density="compact"
                    :disabled="action.disabled"
                    @click="() => handleCustomAction(action)"
                >
                    <BListItemTitle class="d-flex align-center gc-2">
                        <BIcon
                            size="small"
                            :color="action.color || 'data-table-actions'"
                        >
                            {{ action.icon }}
                        </BIcon>
                        <span class="label">{{ action.label }}</span>
                    </BListItemTitle>
                </BListItem>

                <BListItem
                    v-if="showDeleteAction"
                    density="compact"
                    @click="handleDelete"
                >
                    <BListItemTitle class="d-flex align-center gc-2">
                        <BIcon
                            size="small"
                            color="error"
                        >
                            {{ deleteIcon }}
                        </BIcon>
                        <span class="label">{{ t('common.delete') }}</span>
                    </BListItemTitle>
                </BListItem>
            </template>
        </BMenu>
    </div>
</template>

<style scoped lang="scss">
.label {
    font-size: 0.9rem;
}
</style>
